import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiArrowRight,
  FiCheck,
  FiChevronDown,
  FiEdit2,
  FiFileText,
  FiInfo,
  FiLoader,
} from "react-icons/fi";
import PayoutAuthenticatorVerification from "./PayoutAuthenticatorVerification";
import PayoutButton from "./PayoutButton";
import PayoutFlowSteps from "./PayoutFlowSteps";
import { cx, payoutUi } from "./payoutDesignSystem";
import { isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  stableIdempotencyKey,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import {
  KNOWN_PAYOUT_BANK_OPTIONS,
  isIfscBankMismatch,
  isSelectedValue,
  isValidIfsc,
  normalizeBankAccountNumber,
  normalizeIfsc,
  validateRequiredBankAccountNumber,
  validateRequiredConfirmAccountNumber,
  validateRequiredIfsc,
  validateRequiredText,
} from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";
import { contextualValidationMessage } from "./payoutAlertMessages";

type AccountForm = {
  nickname: string;
  accountType: string;
  entity: string;
  currency: string;
  bankName: string;
  holderName: string;
  accountNumber: string;
  confirmAccountNumber: string;
  ifsc: string;
  branchName: string;
  branchCity: string;
  useForPayouts: boolean;
  useForSettlements: boolean;
  primary: boolean;
  status: string;
};

type AccountFieldErrors = Partial<Record<keyof AccountForm, string>>;

type IfscStatus = "idle" | "checking" | "valid" | "error";

const initialForm: AccountForm = {
  nickname: "",
  accountType: "Select account type",
  entity: "Select school / entity",
  currency: "Select currency",
  bankName: "Select bank",
  holderName: "",
  accountNumber: "",
  confirmAccountNumber: "",
  ifsc: "",
  branchName: "",
  branchCity: "",
  useForPayouts: true,
  useForSettlements: true,
  primary: true,
  status: "Active",
};

function FieldLabel({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="mb-2 block text-xs font-semibold text-[#384B88]">
      {children}
      {required && <span className="ml-1 text-[#EF4444]">*</span>}
    </label>
  );
}

function TextInput({
  label,
  value,
  placeholder,
  onChange,
  required,
  error,
  inputMode,
  maxLength,
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
}) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        className={
          "h-11 w-full rounded-[6px] border bg-white px-4 text-sm font-semibold text-[#1B163B] outline-none placeholder:text-[#9CA8CC] focus:border-[#6687FF] " +
          (error ? "border-[#EF4444]" : "border-[#D8E0F0]")
        }
      />
      {error && <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>}
    </div>
  );
}

function SelectInput({
  label,
  value,
  options,
  onChange,
  required,
  error,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div className="relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={
            "h-11 w-full appearance-none rounded-[6px] border bg-white px-4 pr-10 text-sm font-semibold text-[#1B163B] outline-none focus:border-[#6687FF] " +
            (error ? "border-[#EF4444]" : "border-[#D8E0F0]")
          }
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#4B5C93]" />
      </div>
      {error && <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>}
    </div>
  );
}

function CheckboxInput({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 text-sm font-semibold text-[#1B163B]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />
      <span
        className={
          "flex h-5 w-5 items-center justify-center rounded-[5px] border text-xs " +
          (checked
            ? "border-[#3B67F6] bg-[#3B67F6] text-white"
            : "border-[#C9D2E8] bg-white text-transparent")
        }
      >
        <FiCheck />
      </span>
      {label}
    </label>
  );
}

function Stepper({ activeStep }: { activeStep: number }) {
  const steps = ["Account Details", "Verification", "Review & Confirm"];

  return <PayoutFlowSteps steps={steps} activeStep={activeStep} framed={false} />;
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 border-b border-[#E0E5F2] px-4 py-4 last:border-b-0">
      <span className="text-sm font-semibold text-[#1B163B]">{label}</span>
      <span className="text-sm font-semibold text-[#1B163B]">{value}</span>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-5 border-b border-[#E0E5F2] py-4 last:border-b-0">
      <span className="text-sm font-medium text-[#505E8E]">{label}</span>
      <span className="max-w-[360px] text-right text-sm font-semibold text-[#1B163B]">
        {value}
      </span>
    </div>
  );
}

function ReviewSection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
      <div className="mb-2 flex items-center justify-between gap-4">
        <h2 className="text-[18px] font-semibold text-[#1B163B]">{title}</h2>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-9 items-center gap-2 rounded-[6px] border border-[#D8E0F0] bg-white px-3 text-sm font-semibold text-[#3366FF]"
        >
          <FiEdit2 />
          Edit
        </button>
      </div>
      <div>{children}</div>
    </section>
  );
}

const displayText = (value: string) =>
  value && !value.toLowerCase().startsWith("select") ? value : "-";

const maskedAccountNumber = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "-";
  return `**** ${digits.slice(-4)}`;
};

const apiString = (record: Record<string, unknown>, keys: string[], fallback = "") => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return fallback;
};

function PayoutAddAccount() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState(initialForm);
  const [activeStep, setActiveStep] = React.useState(1);
  const [fieldErrors, setFieldErrors] = React.useState<AccountFieldErrors>({});
  const [ifscStatus, setIfscStatus] = React.useState<IfscStatus>("idle");
  const [ifscMessage, setIfscMessage] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const accountTypeSummary =
    form.accountType === "Select account type" ? "-" : form.accountType;
  const currencySummary = form.currency === "Select currency" ? "-" : form.currency;
  const intendedUseSummary = [
    form.useForPayouts && "Payouts",
    form.useForSettlements && "Settlements",
  ]
    .filter(Boolean)
    .join(", ") || "-";
  const updateForm =
    <K extends keyof AccountForm>(key: K) =>
    (value: AccountForm[K]) => {
      const nextValue =
        key === "ifsc"
          ? normalizeIfsc(value)
          : key === "accountNumber" || key === "confirmAccountNumber"
            ? normalizeBankAccountNumber(value)
            : value;
      setForm((current) => ({ ...current, [key]: nextValue as AccountForm[K] }));
      setFieldErrors((current) => {
        const shouldClearUsageError = key === "useForPayouts" || key === "useForSettlements";
        if (!current[key] && !(shouldClearUsageError && current.useForPayouts)) {
          return current;
        }
        const next = { ...current };
        delete next[key];
        if (shouldClearUsageError) delete next.useForPayouts;
        return next;
      });
    };

  React.useEffect(() => {
    const normalizedIfsc = normalizeIfsc(form.ifsc);

    if (!normalizedIfsc) {
      setIfscStatus("idle");
      setIfscMessage("Enter IFSC to detect bank details.");
      return;
    }

    if (!isValidIfsc(normalizedIfsc)) {
      setIfscStatus("error");
      setIfscMessage("Use the standard IFSC format, e.g. HDFC0001234.");
      return;
    }

    if (isIfscBankMismatch(form.bankName, normalizedIfsc)) {
      setIfscStatus("error");
      setIfscMessage("IFSC bank code does not match the selected bank.");
      return;
    }

    if (!isPayoutApiMode()) {
      setIfscStatus("valid");
      setIfscMessage("IFSC format looks valid.");
      return;
    }

    const controller = new AbortController();
    const lookupTimer = window.setTimeout(() => {
      setIfscStatus("checking");
      setIfscMessage("Checking IFSC with bank records...");
      payoutApi
        .ifscLookup(normalizedIfsc, controller.signal)
        .then((payload) => {
          const bankName = apiString(payload, ["bank", "bank_name", "bankName", "name"]);
          const branchName = apiString(payload, ["branch", "branch_name", "branchName"]);
          const branchCity = apiString(payload, ["city", "branch_city", "branchCity"]);
          const matchingOption = KNOWN_PAYOUT_BANK_OPTIONS.find(
            (option) => option.toLowerCase() === bankName.toLowerCase(),
          );

          setForm((current) => {
            if (current.ifsc !== normalizedIfsc) return current;
            return {
              ...current,
              bankName:
                isSelectedValue(current.bankName) || !matchingOption
                  ? current.bankName
                  : matchingOption,
              branchName: current.branchName.trim() || branchName || current.branchName,
              branchCity: current.branchCity.trim() || branchCity || current.branchCity,
            };
          });
          setIfscStatus("valid");
          setIfscMessage(
            branchName
              ? `Bank details verified for ${branchName}.`
              : "Bank details verified from IFSC.",
          );
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.warn("IFSC lookup failed", error);
          setIfscStatus("valid");
          setIfscMessage("IFSC format looks valid. Bank will verify it on save.");
        });
    }, 450);

    return () => {
      window.clearTimeout(lookupTimer);
      controller.abort();
    };
  }, [form.bankName, form.ifsc]);

  const validateAccountDetails = async () => {
    const nextErrors: AccountFieldErrors = {};
    const normalizedIfsc = normalizeIfsc(form.ifsc);
    const accountNumber = normalizeBankAccountNumber(form.accountNumber);

    nextErrors.nickname = validateRequiredText(form.nickname, "Enter account nickname.", {
      value: 3,
      message: "Account nickname must be at least 3 characters.",
    });
    if (!isSelectedValue(form.accountType)) nextErrors.accountType = "Select account type.";
    if (!isSelectedValue(form.entity)) nextErrors.entity = "Select school / entity.";
    if (!isSelectedValue(form.currency)) nextErrors.currency = "Select currency.";
    if (!isSelectedValue(form.bankName)) nextErrors.bankName = "Select bank name.";
    nextErrors.holderName = validateRequiredText(form.holderName, "Enter account holder name.", {
      value: 3,
      message: "Account holder name must be at least 3 characters.",
    });
    nextErrors.accountNumber = validateRequiredBankAccountNumber(
      form.accountNumber,
      "Enter account number.",
    );
    nextErrors.confirmAccountNumber = validateRequiredConfirmAccountNumber(
      form.confirmAccountNumber,
      form.accountNumber,
      "Re-enter account number.",
    );
    nextErrors.ifsc = validateRequiredIfsc(form.ifsc, form.bankName);
    if (!form.branchName.trim()) nextErrors.branchName = "Enter branch name.";
    if (!form.branchCity.trim()) nextErrors.branchCity = "Enter branch city.";
    if (!form.useForPayouts && !form.useForSettlements) {
      nextErrors.useForPayouts = "Choose at least one intended use.";
    }

    const compactErrors = Object.fromEntries(
      Object.entries(nextErrors).filter(([, value]) => value),
    ) as AccountFieldErrors;
    setFieldErrors(compactErrors);

    if (Object.keys(compactErrors).length) {
      showPayoutNotification({
        tone: "error",
        message: contextualValidationMessage(
          "Create payout account",
          compactErrors,
          "Create payout account: complete the highlighted account and bank details.",
        ),
      });
      return false;
    }

    if (!isPayoutApiMode()) return true;

    try {
      await payoutApi.validateAccount(
        compactBody({
          account_number: accountNumber,
          ifsc: normalizedIfsc,
          bank_name: form.bankName,
          account_holder_name: form.holderName.trim(),
        }),
        stableIdempotencyKey("validate-account", `${accountNumber}-${normalizedIfsc}`),
      );
      setIfscStatus("valid");
      setIfscMessage("Bank details verified.");
      return true;
    } catch (error) {
      setFieldErrors((current) => ({
        ...current,
        ifsc: "Bank account validation failed. Check account number and IFSC.",
      }));
      showPayoutNotification({
        tone: "error",
        message: errorMessage(error, "Bank account validation failed."),
      });
      return false;
    }
  };

  const createAccountPayload = () =>
    compactBody({
      display_name: form.nickname.trim(),
      name: form.nickname.trim(),
      account_type: form.accountType,
      school_name: form.entity,
      currency: form.currency,
      bank_name: form.bankName,
      account_holder_name: form.holderName.trim(),
      account_number: normalizeBankAccountNumber(form.accountNumber),
      ifsc: normalizeIfsc(form.ifsc),
      branch_name: form.branchName.trim(),
      branch_city: form.branchCity.trim(),
      use_for_payouts: form.useForPayouts,
      use_for_settlements: form.useForSettlements,
      is_primary: form.primary,
      status: form.status.toUpperCase(),
    });

  const handleCreateAccount = async () => {
    const payload = createAccountPayload();
    setSubmitting(true);
    try {
      if (isPayoutApiMode()) {
        await payoutApi.createAccount(
          payload,
          stableIdempotencyKey(
            "create-account",
            `${payload.account_number}-${payload.ifsc}`,
          ),
        );
      } else {
        writeSessionJson("payoutCreatedAccountDraft", payload);
      }
      showPayoutNotification({
        tone: "success",
        message: "Payout account added successfully.",
      });
      navigate("/payouts/accounts");
    } catch (error) {
      showPayoutNotification({
        tone: "error",
        message: errorMessage(error, "Unable to add payout account."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    if (activeStep === 1) {
      const valid = await validateAccountDetails();
      if (!valid) return;
    }
    if (activeStep === 3) {
      await handleCreateAccount();
      return;
    }
    setActiveStep((current) => Math.min(current + 1, 3));
  };

  const ifscHelp =
    fieldErrors.ifsc ||
    ifscMessage ||
    (form.ifsc ? "Use the standard IFSC format, e.g. HDFC0001234." : "Enter IFSC to detect bank details.");
  const ifscHelpClass =
    ifscStatus === "error" || fieldErrors.ifsc
      ? "text-[#EF4444]"
      : ifscStatus === "valid"
        ? "text-[#16A34A]"
        : "text-[#505E8E]";

  return (
    <form onSubmit={handleSubmit} className="w-full px-4 pt-4 pb-8">
      <div className="w-full">
        <div className="mb-5">
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Create New Payout Account
          </h1>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            Add a new school payout account to be used for beneficiary payments and transfers.
          </p>
        </div>

        <div className={payoutUi.flowActionBar}>
          <Stepper activeStep={activeStep} />
          <div className="ml-auto flex items-center gap-3">
            <Link
              to="/payouts/accounts"
              className={cx(payoutUi.button.base, payoutUi.button.secondary, "min-w-[120px] px-6")}
            >
              Cancel
            </Link>
            <PayoutButton
              type="submit"
              variant="primary"
              disabled={submitting}
              className="min-w-[180px] px-6"
            >
              {submitting
                ? "Saving..."
                : activeStep === 1
                ? "Verify & Continue"
                : activeStep === 2
                  ? "Review & Continue"
                  : "Create Account"}
              {submitting ? <FiLoader className="animate-spin" /> : activeStep < 3 && <FiArrowRight />}
            </PayoutButton>
          </div>
        </div>

        <div className="grid max-w-[1132px] gap-5 xl:grid-cols-[minmax(0,704px)_408px]">
          <div className="space-y-5">
            {activeStep === 1 ? (
              <>
                <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
                  <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
                    1. Account Details
                  </h2>
                  <div className="grid gap-5 md:grid-cols-2">
                    <TextInput
                      label="Account Nickname"
                      value={form.nickname}
                      placeholder="Enter account nickname"
                      onChange={updateForm("nickname")}
                      error={fieldErrors.nickname}
                      required
                    />
                    <SelectInput
                      label="Account Type"
                      value={form.accountType}
                      options={["Select account type", "Current Account", "Savings Account"]}
                      onChange={updateForm("accountType")}
                      error={fieldErrors.accountType}
                      required
                    />
                    <SelectInput
                      label="School / Entity"
                      value={form.entity}
                      options={["Select school / entity", "Local Payouts Team", "Edviron School"]}
                      onChange={updateForm("entity")}
                      error={fieldErrors.entity}
                      required
                    />
                    <SelectInput
                      label="Currency"
                      value={form.currency}
                      options={["Select currency", "INR"]}
                      onChange={updateForm("currency")}
                      error={fieldErrors.currency}
                      required
                    />
                  </div>
                </section>

                <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
                  <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
                    2. Bank Information
                  </h2>
                  <div className="grid gap-5 md:grid-cols-2">
                    <SelectInput
                      label="Bank Name"
                      value={form.bankName}
                      options={KNOWN_PAYOUT_BANK_OPTIONS}
                      onChange={updateForm("bankName")}
                      error={fieldErrors.bankName}
                      required
                    />
                    <TextInput
                      label="Account Holder Name"
                      value={form.holderName}
                      placeholder="Enter account holder name"
                      onChange={updateForm("holderName")}
                      error={fieldErrors.holderName}
                      required
                    />
                    <TextInput
                      label="Account Number"
                      value={form.accountNumber}
                      placeholder="Enter account number"
                      onChange={updateForm("accountNumber")}
                      error={fieldErrors.accountNumber}
                      inputMode="numeric"
                      maxLength={18}
                      required
                    />
                    <TextInput
                      label="Re-enter Account Number"
                      value={form.confirmAccountNumber}
                      placeholder="Re-enter account number"
                      onChange={updateForm("confirmAccountNumber")}
                      error={fieldErrors.confirmAccountNumber}
                      inputMode="numeric"
                      maxLength={18}
                      required
                    />
                    <div>
                      <TextInput
                        label="IFSC Code"
                        value={form.ifsc}
                        placeholder="Enter IFSC code"
                        onChange={updateForm("ifsc")}
                        error={fieldErrors.ifsc}
                        maxLength={11}
                        required
                      />
                      <div className={`mt-2 flex min-h-[20px] items-center gap-2 text-xs font-semibold ${ifscHelpClass}`}>
                        {ifscStatus === "checking" ? (
                          <FiLoader className="shrink-0 animate-spin" />
                        ) : ifscStatus === "valid" ? (
                          <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#16A34A]">
                            <FiCheck className="text-[10px]" />
                          </span>
                        ) : ifscStatus === "error" || fieldErrors.ifsc ? (
                          <FiAlertCircle className="shrink-0" />
                        ) : (
                          <FiInfo className="shrink-0" />
                        )}
                        <span>{ifscHelp}</span>
                      </div>
                    </div>
                    <TextInput
                      label="Branch Name"
                      value={form.branchName}
                      placeholder="Enter branch name"
                      onChange={updateForm("branchName")}
                      error={fieldErrors.branchName}
                      required
                    />
                    <div className="md:col-span-2">
                      <TextInput
                        label="Branch City"
                        value={form.branchCity}
                        placeholder="Enter branch city"
                        onChange={updateForm("branchCity")}
                        error={fieldErrors.branchCity}
                        required
                      />
                    </div>
                  </div>
                </section>

                <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
                  <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
                    3. Usage & Controls
                  </h2>
                  <div className="grid gap-5 lg:grid-cols-[repeat(3,minmax(0,1fr))_180px] lg:items-end">
                    <CheckboxInput
                      label="Use for payouts"
                      checked={form.useForPayouts}
                      onChange={updateForm("useForPayouts")}
                    />
                    <CheckboxInput
                      label="Use for settlements"
                      checked={form.useForSettlements}
                      onChange={updateForm("useForSettlements")}
                    />
                    <CheckboxInput
                      label="Set as primary account"
                      checked={form.primary}
                      onChange={updateForm("primary")}
                    />
                    <SelectInput
                      label="Status"
                      value={form.status}
                      options={["Active", "Inactive"]}
                      onChange={updateForm("status")}
                      required
                    />
                  </div>
                  {fieldErrors.useForPayouts && (
                    <p className="mt-4 text-xs font-semibold text-[#EF4444]">
                      {fieldErrors.useForPayouts}
                    </p>
                  )}
                </section>
              </>
            ) : activeStep === 2 ? (
              <PayoutAuthenticatorVerification
                title="Verify Payout Account"
                description="Enter the 6-digit code from the configured Google Authenticator app."
                info="This verification protects payout account setup, beneficiary payments and transfer permissions."
                inputIdPrefix="payout-account-otp"
              />
            ) : (
              <>
                <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
                  <div className="mb-5 flex items-start gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#DCFCE7] text-xl text-[#16A34A]">
                      <FiCheck />
                    </span>
                    <div>
                      <h2 className="text-[18px] font-semibold text-[#1B163B]">
                        Review & Confirm Payout Account
                      </h2>
                      <p className="mt-2 text-sm font-medium text-[#505E8E]">
                        Review the payout account details before saving this account.
                      </p>
                    </div>
                  </div>
                  <div className="rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-semibold text-[#166534]">
                    Google Authenticator verification completed. This account is ready for final review.
                  </div>
                </section>

                <ReviewSection
                  title="Account Details"
                  onEdit={() => setActiveStep(1)}
                >
                  <ReviewRow label="Account Nickname" value={displayText(form.nickname)} />
                  <ReviewRow label="Account Type" value={displayText(form.accountType)} />
                  <ReviewRow label="School / Entity" value={displayText(form.entity)} />
                  <ReviewRow label="Currency" value={displayText(form.currency)} />
                </ReviewSection>

                <ReviewSection
                  title="Bank Information"
                  onEdit={() => setActiveStep(1)}
                >
                  <ReviewRow label="Bank Name" value={displayText(form.bankName)} />
                  <ReviewRow label="Account Holder Name" value={displayText(form.holderName)} />
                  <ReviewRow label="Account Number" value={maskedAccountNumber(form.accountNumber)} />
                  <ReviewRow label="IFSC Code" value={displayText(form.ifsc)} />
                  <ReviewRow label="Branch Name" value={displayText(form.branchName)} />
                  <ReviewRow label="Branch City" value={displayText(form.branchCity)} />
                </ReviewSection>

                <ReviewSection
                  title="Usage & Controls"
                  onEdit={() => setActiveStep(1)}
                >
                  <ReviewRow label="Intended Use" value={intendedUseSummary} />
                  <ReviewRow
                    label="Primary Account"
                    value={form.primary ? "Yes" : "No"}
                  />
                  <ReviewRow label="Status" value={form.status} />
                  <ReviewRow
                    label="Verification"
                    value={
                      <span className="inline-flex rounded-full bg-[#DCFCE7] px-3 py-1 text-xs font-semibold text-[#166534]">
                        Verified
                      </span>
                    }
                  />
                </ReviewSection>
              </>
            )}
          </div>

          <aside className="space-y-5">
            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
              <div className="mb-5 flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#DDE6FF] text-xl text-[#1B163B]">
                  <FiFileText />
                </span>
                <h2 className="text-[16px] font-semibold text-[#1B163B]">
                  Account Setup Summary
                </h2>
              </div>
              <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2]">
                <SummaryRow label="Account Type" value={accountTypeSummary} />
                <SummaryRow label="Currency" value={currencySummary} />
                <SummaryRow label="Intended Use" value={intendedUseSummary} />
                <SummaryRow
                  label="Verification Status"
                  value={
                    <span
                      className={
                        "inline-flex rounded-full px-3 py-1 text-xs " +
                        (activeStep === 3
                          ? "bg-[#DCFCE7] text-[#166534]"
                          : "bg-[#FFE8D6] text-[#9A4A12]")
                      }
                    >
                      {activeStep === 3 ? "Verified" : "Pending"}
                    </span>
                  }
                />
              </div>
            </section>

            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
              <div className="mb-5 flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#DDE6FF] text-xl text-[#3366FF]">
                  <FiInfo />
                </span>
                <h2 className="text-[16px] font-semibold text-[#1B163B]">
                  What happens next?
                </h2>
              </div>
              <ul className="space-y-4 text-sm font-medium text-[#1B163B]">
                <li className="flex gap-3">
                  <span>{"\u2022"}</span>
                  Bank account will be verified before activation
                </li>
                {activeStep < 3 && (
                  <li className="flex gap-3">
                    <span>{"\u2022"}</span>
                    You can review details before saving
                  </li>
                )}
                <li className="flex gap-3">
                  <span>{"\u2022"}</span>
                  Primary account can be changed later
                </li>
              </ul>
            </section>
          </aside>
        </div>
      </div>

    </form>
  );
}

export default PayoutAddAccount;
